/**
 * YouTubeEmbed — drives a YouTube video via the official IFrame Player API.
 *
 * Why not a plain <iframe>?
 *   • Cross-origin postMessage commands require YouTube's ready handshake.
 *     Without it, our playVideo() / unMute() commands are silently dropped
 *     and the user is left staring at a stuck "play" button.
 *   • The official API gives us reliable play / pause / mute / unmute
 *     control, plus a `controls: 0` mode so YouTube's own play overlay
 *     doesn't sit forever in the centre of the card.
 *
 * The API library is loaded lazily once per page (≈60 KB, cached).
 * Multiple cards share the same loader promise so we never re-fetch it.
 */
import React, { useEffect, useRef } from "react";
import { plyxApi } from "../../lib/plyxApi";

// iter-124: lightweight boot telemetry. Real devices report where the
// embed boot pipeline stalls (API load, player boot, first frame,
// errors) so we never have to debug "videos don't play on my phone"
// blind again. Batched + fire-and-forget; a failed send is ignored.
let _diagBuf = [];
let _diagTimer = null;
function diag(ev, vid, extra) {
  try {
    _diagBuf.push({ ev, vid, t: Date.now(), ...(extra || {}) });
    if (_diagBuf.length > 40) _diagBuf = _diagBuf.slice(-40);
    if (!_diagTimer) {
      _diagTimer = setTimeout(() => {
        _diagTimer = null;
        const events = _diagBuf.splice(0);
        if (!events.length) return;
        plyxApi.post("/plyx/diag/embed", { events, ua: navigator.userAgent }).catch(() => {});
      }, 4000);
    }
  } catch { /* ignore */ }
}

// iOS Safari has a long-standing quirk where YouTube's iframe player
// reports `playing` and pipes audio through, but never actually
// paints the <video> element on screen (especially with autoplay=1 +
// muted=1 + controls=0). Every user we've heard from running into
// "blank video, only sound" was on an iPhone. Detect once at module
// load and keep the high-res YouTube thumbnail visible above the
// iframe on those devices — pragmatic workaround that gives the
// user something to look at + a clear tap target to open the full
// clip in the YouTube app.
const _IS_IOS = typeof navigator !== "undefined"
  && /iPad|iPhone|iPod/.test(navigator.userAgent)
  && !window.MSStream;

// Lazy global loader — returns a promise that resolves to `window.YT`.
// Re-used across every card so we never download the script twice.
let _ytApiPromise = null;
function loadYouTubeIframeAPI() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (_ytApiPromise) return _ytApiPromise;
  _ytApiPromise = new Promise((resolve) => {
    // YouTube calls this global when the API has booted.
    const prior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try { prior && prior(); } catch { /* ignore */ }
      resolve(window.YT);
    };
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api";
    s.async = true;
    document.head.appendChild(s);
  });
  return _ytApiPromise;
}

export default function YouTubeEmbed({
  videoId,
  isActive,
  startMuted = true,
  bootDelay = 180,
  onReady,
  onPlayingChange,
  onEmbedError,
  onBootFailed,
}) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const readyRef = useRef(false);
  // iter-121: live mirrors of the latest props. The onReady/onStateChange
  // closures below are created ONCE (effect deps [videoId]) — reading the
  // props directly in them sees STALE values whenever the card became
  // active while the player was still booting (1–3s on mobile). That race
  // paused the ACTIVE card right as it finished booting, leaving a black
  // tile that only recovered after swiping away and back. Refs always
  // hold the current value.
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const startMutedRef = useRef(startMuted);
  startMutedRef.current = startMuted;
  // Track whether we've ever seen the YT player report PLAYING state.
  // We use this to fade the poster overlay out only AFTER the player
  // has actually painted a real frame — guarantees the user never
  // sees a black flash on mobile Safari while the iframe boots.
  const [hasPainted, setHasPainted] = React.useState(false);
  // iter-123: bumping bootId destroys + recreates the player — the
  // self-heal path for wedged iframe boots (network flap, dropped
  // postMessage handshake). One reboot allowed per activation.
  const [bootId, setBootId] = React.useState(0);

  useEffect(() => {
    let cancelled = false;
    let player;
    bootT0Ref.current = Date.now();
    diag("boot_start", videoId, { delay: bootDelay, active: isActiveRef.current });
    // iter-123/124: defer the heavy iframe boot so it never lands in the
    // middle of the swipe/snap animation, and STAGGER neighbours (next
    // +400ms, prev +1200ms) so three players don't fight the active one
    // for bandwidth and decoder slots on phones.
    const bootTimer = setTimeout(() => {
      loadYouTubeIframeAPI().then((YT) => {
        if (cancelled || !YT || !containerRef.current) return;
        diag("api_ready", videoId, { dt: Date.now() - bootT0Ref.current });
        // iter-123: YT.Player REPLACES the node it's given with its
        // iframe, which breaks React reconciliation and reboots if we
        // hand it a React-rendered node. Create a throwaway mount node
        // imperatively instead.
        const host = containerRef.current;
        host.innerHTML = "";
        const mount = document.createElement("div");
        host.appendChild(mount);
        player = new YT.Player(mount, {
        videoId,
        playerVars: {
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          // CRITICAL — on mobile Safari, YouTube's iframe does NOT
          // render its internal <video> element until playback is
          // actually triggered. With `autoplay: 0` the user sees a
          // permanently black box even though the iframe DOM node is
          // present. Muted autoplay is allowed everywhere (iOS/Android/
          // Chrome/Safari) and immediately materialises the video
          // surface, so the user sees frames as soon as the card
          // becomes visible. Cards that aren't `isActive` are
          // immediately paused below — bandwidth cost is one ~2s
          // preroll segment per card and well worth it.
          autoplay: 1,
          mute: 1,
          enablejsapi: 1,
        },
        events: {
          onReady: (e) => {
            readyRef.current = true;
            playerRef.current = e.target;
            diag("on_ready", videoId, { dt: Date.now() - bootT0Ref.current });
            // iter-121: decide from the LIVE ref, not the stale prop —
            // the card may have become active while the player booted.
            if (isActiveRef.current) {
              try {
                // Always start from the top — never resume from a
                // stale position. Matches TikTok/Reels behaviour and
                // is what creators expect when their clip loops on
                // re-entry into the feed.
                e.target.seekTo(0, true);
                // iter-124: ALWAYS start muted. Android Chrome (and
                // iOS Safari) BLOCK unmuted autoplay without a fresh
                // user gesture — when the viewer's saved pref was
                // "sound on", the old immediate unMute() wedged the
                // player before it ever started (the "spinner never
                // stops" report). We unmute in onStateChange the
                // moment PLAYING actually fires instead.
                e.target.mute && e.target.mute();
                e.target.playVideo();
              } catch { /* ignore */ }
            } else {
              try { e.target.pauseVideo(); } catch { /* ignore */ }
            }
            onReady && onReady(e.target);
          },
          onStateChange: (e) => {
            const playing = e.data === 1;
            onPlayingChange && onPlayingChange(playing);
            if (playing && !hasPaintedRef.current) {
              diag("first_playing", videoId, { dt: Date.now() - bootT0Ref.current });
            }
            // First time the YT player actually starts painting frames,
            // fade out the thumbnail overlay — guarantees the user
            // never sees a black flash on mobile Safari while the
            // iframe boots.
            if (playing) setHasPainted(true);
            if (playing) hasPaintedRef.current = true;
            // iter-124: apply the viewer's sound pref only once playback
            // is confirmed rolling — reliable under every browser
            // autoplay policy (sound arrives ~0.5s after first frame).
            if (playing && !startMutedRef.current) {
              try {
                e.target.unMute();
                e.target.setVolume && e.target.setVolume(100);
              } catch { /* ignore */ }
            }
            // iter-121: self-healing kick — if YouTube lands this card
            // in CUED (5) or UNSTARTED (-1) while it's the ACTIVE slide
            // (happens after slow boots / network blips), nudge playback
            // so the viewer never stares at a frozen poster.
            if ((e.data === 5 || e.data === -1) && isActiveRef.current) {
              try { e.target.playVideo(); } catch { /* ignore */ }
            }
          },
          // ── Embed-blocked detection ──────────────────────────────
          // YouTube fires onError with these specific codes when the
          // uploader has disabled embedding (common on Vevo / music
          // videos), the video is private, geo-restricted, or removed.
          //   2   → invalid videoId
          //   5   → HTML5 player not allowed
          //   100 → video not found / private / removed
          //   101 → embed disabled by owner
          //   150 → equivalent to 101
          // Surface to the parent so it can render a "Watch on YouTube"
          // fallback instead of leaving the user staring at a black box.
          onError: (e) => {
            const code = e?.data;
            diag("yt_error", videoId, { code });
            if ([2, 5, 100, 101, 150].includes(code)) {
              onEmbedError && onEmbedError(code);
            }
          },
        },
      });
      });
    }, bootDelay);
    return () => {
      cancelled = true;
      clearTimeout(bootTimer);
      try { player && player.destroy && player.destroy(); } catch { /* ignore */ }
      try { if (containerRef.current) containerRef.current.innerHTML = ""; } catch { /* ignore */ }
      playerRef.current = null;
      readyRef.current = false;
    };
  }, [videoId, bootId]);

  // iter-125/127: PROGRESS-AWARE BOOT WATCHDOG. iter-125's version
  // rebooted any non-playing player at 4.5s/9s and auto-skipped at a
  // fixed 14s — on slow mobile data that destroyed players that were
  // legitimately still DOWNLOADING, so nothing ever played ("is worse,
  // all hang"). We now sample getVideoLoadedFraction() every 4.5s:
  //   • fraction grew → data is flowing → unlimited patience.
  //   • two consecutive stalled checks → destroy + recreate (max 2).
  //   • three more stalled checks after both reboots → onBootFailed
  //     (parent auto-skips). Truly dead embeds usually fire onError
  //     first and are removed via that faster path anyway.
  const rebootsRef = useRef(0);
  const noProgressRef = useRef(0);
  const lastFracRef = useRef(-1);
  const hasPaintedRef = useRef(false);
  const bootT0Ref = useRef(Date.now());
  const onBootFailedRef = useRef(onBootFailed);
  onBootFailedRef.current = onBootFailed;
  useEffect(() => {
    if (!isActive) {
      rebootsRef.current = 0;
      noProgressRef.current = 0;
      lastFracRef.current = -1;
      return;
    }
    const iv = setInterval(() => {
      if (hasPaintedRef.current) return;
      const p = playerRef.current;
      let st = null;
      let frac = 0;
      if (p) {
        try { st = p.getPlayerState ? p.getPlayerState() : null; } catch { st = "threw"; }
        try { frac = p.getVideoLoadedFraction ? (p.getVideoLoadedFraction() || 0) : 0; } catch { frac = 0; }
      }
      if (st === 0 || st === 1) { noProgressRef.current = 0; return; }
      if (frac > lastFracRef.current + 0.0005) {
        lastFracRef.current = frac;
        noProgressRef.current = 0;   // downloading — be patient, never kill a slow load
        return;
      }
      noProgressRef.current += 1;
      if (noProgressRef.current >= 2 && rebootsRef.current < 2) {
        rebootsRef.current += 1;
        noProgressRef.current = 0;
        lastFracRef.current = -1;
        diag("watchdog_reboot", videoId, { st: String(st), n: rebootsRef.current });
        setHasPainted(false);
        setBootId((n) => n + 1);
        return;
      }
      if (noProgressRef.current >= 3 && rebootsRef.current >= 2) {
        noProgressRef.current = -9999;  // fire once
        diag("boot_failed", videoId, { dt: Date.now() - bootT0Ref.current });
        try { onBootFailedRef.current && onBootFailedRef.current(); } catch { /* ignore */ }
      }
    }, 4500);
    return () => clearInterval(iv);
  }, [isActive, videoId]);

  // iter-128: MID-PLAYBACK freeze recovery. After first paint a YT
  // player can wedge in endless buffering (state 3) with the clock
  // frozen. Buffering means it WANTS to play (so this never fights a
  // user pause) — if the clock hasn't moved across two 5s checks we
  // nudge it with seekTo(+0.1)+play, up to 3 times per activation.
  const playTRef = useRef(-1);
  const freezeNudgesRef = useRef(0);
  useEffect(() => {
    if (!isActive) { playTRef.current = -1; freezeNudgesRef.current = 0; return; }
    let stuckChecks = 0;
    const iv = setInterval(() => {
      if (!hasPaintedRef.current) return;
      const p = playerRef.current;
      if (!p) return;
      let st = null;
      let t = -1;
      try { st = p.getPlayerState ? p.getPlayerState() : null; } catch { return; }
      try { t = p.getCurrentTime ? (p.getCurrentTime() || 0) : -1; } catch { return; }
      if (st !== 3 || Math.abs(t - playTRef.current) > 0.2) {
        stuckChecks = 0;
        playTRef.current = t;
        return;
      }
      stuckChecks += 1;
      if (stuckChecks >= 2 && freezeNudgesRef.current < 3) {
        stuckChecks = 0;
        freezeNudgesRef.current += 1;
        diag("freeze_nudge", videoId, { t: Math.round(t), n: freezeNudgesRef.current });
        try { p.seekTo(Math.max(0, t + 0.1), true); } catch { /* ignore */ }
        try { p.playVideo(); } catch { /* ignore */ }
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [isActive, videoId]);

  // iter-124: spinner cap — if the active card STILL hasn't painted 10s
  // after activation, stop spinning and offer "Watch on YouTube" so it
  // never feels like an endless network hang.
  const [bootTimedOut, setBootTimedOut] = React.useState(false);
  useEffect(() => {
    if (!isActive || hasPainted) { setBootTimedOut(false); return; }
    const t = setTimeout(() => {
      setBootTimedOut(true);
      diag("boot_timeout", videoId, {});
    }, 10000);
    return () => clearTimeout(t);
  }, [isActive, hasPainted, bootId, videoId]);

  // Forward isActive changes to the live player — playVideo when the
  // card becomes the active one, pauseVideo otherwise. This guarantees
  // no two videos play at once even if the iframe lifecycle is slow.
  //
  // On EVERY swipe-back to an already-visited card we also seek the
  // player back to 0 so the clip restarts from the top (TikTok / Reels
  // behaviour). Previously YouTube held the last paused position so
  // the user would mid-clip on revisit — the exact "starts from where
  // it stopped" complaint in iter-111. Pairing seekTo + playVideo also
  // forces mobile Safari to wake the iframe up reliably; calling
  // playVideo on its own sometimes silently no-ops on Safari.
  //
  // iter-116 audio-regression fix: `seekTo` on iOS Safari has been
  // observed to silently revert the player to muted state, which is
  // why users reported "no sound on any clip after deploying" — every
  // swipe was implicitly re-muting the player. We now ALWAYS re-apply
  // the desired mute state right after seek+play.
  useEffect(() => {
    const p = playerRef.current;
    if (!p || !readyRef.current) return;
    try {
      if (isActive) {
        try { p.seekTo(0, true); } catch { /* ignore */ }
        // iter-124: muted-first playback for players that haven't
        // proven playable yet — unmute happens on the PLAYING event
        // (see onStateChange). Painted players get the pref applied
        // directly; by then the page has real user activation from
        // the swipe itself.
        if (!hasPaintedRef.current) {
          try { p.mute && p.mute(); } catch { /* ignore */ }
          p.playVideo();
        } else {
          p.playVideo();
          try {
            if (startMuted) {
              p.mute && p.mute();
            } else {
              p.unMute && p.unMute();
              p.setVolume && p.setVolume(100);
            }
          } catch { /* ignore */ }
        }
      } else {
        p.pauseVideo();
      }
    } catch { /* ignore */ }
  }, [isActive, startMuted]);

  // Dedicated mute-state effect — when the viewer flips the global
  // mute pref (tapping the speaker pill or "Tap for sound") the
  // CURRENT player needs to sync immediately, not wait for the next
  // swipe. Decoupled from the isActive effect so toggling mute never
  // resets playback to the top.
  useEffect(() => {
    const p = playerRef.current;
    if (!p || !readyRef.current) return;
    try {
      if (startMuted) {
        p.mute && p.mute();
      } else {
        p.unMute && p.unMute();
        p.setVolume && p.setVolume(100);
      }
    } catch { /* ignore */ }
  }, [startMuted]);

  // Expose imperative handle pattern — parent can call these via window
  // events but we keep this minimal. For mute toggling the parent grabs
  // the player handle from `onReady`.

  return (
    <div className="absolute inset-0 w-full h-full">
      {/* YT.Player will replace this node with an iframe. */}
      <div ref={containerRef} className="w-full h-full" />
      {/* Poster overlay — high-res YouTube thumbnail painted on TOP of
          the iframe until YT reports PLAYING. Without this, mobile
          Safari shows a black frame for ~300ms-1s after iframe mount,
          which the user repeatedly perceived as "blank video".

          iter-121: the old behaviour kept this poster PERMANENTLY
          visible on iOS — meaning every YouTube clip on iPhone looked
          like a frozen thumbnail that "does not play" (audio behind a
          static image). Modern iOS Safari paints playsinline embeds
          fine, so the poster now fades on first PLAYING everywhere.
          While the poster is still up on iOS we keep the "Watch on
          YouTube" escape hatch in case a specific clip never paints. */}
      {!hasPainted && (
        <>
          <img
            src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
            onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`; }}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-300"
            data-testid={`plyx-yt-poster-${videoId}`}
          />
          {/* iter-123/124: booting spinner — visible loading progress
              over the poster; capped at 10s (bootTimedOut) so it never
              spins forever like a dead network request. */}
          {isActive && !bootTimedOut && (
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              data-testid={`plyx-yt-booting-${videoId}`}
            >
              <span className="w-12 h-12 rounded-full bg-black/40 backdrop-blur flex items-center justify-center">
                <svg className="w-6 h-6 text-white animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
                  <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </span>
            </div>
          )}
          {(_IS_IOS || bootTimedOut) && (
            <a
              href={`https://www.youtube.com/watch?v=${videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`plyx-yt-open-${videoId}`}
              className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[6] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm border border-white/15 text-white text-[11px] font-semibold shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              ▶︎  Watch on YouTube
            </a>
          )}
        </>
      )}
    </div>
  );
}
